import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

/* ── helpers ── */
const toSlug = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const SECTION_COLORS = {
  juices: { color: "#2D8B4E", bg: "rgba(45,139,78,0.07)" },
  salads: { color: "#16a34a", bg: "rgba(22,163,74,0.07)" },
  sandwiches: { color: "#d97706", bg: "rgba(217,119,6,0.07)" },
  smoothies: { color: "#7c3aed", bg: "rgba(124,58,237,0.07)" },
};
const sColor = (key) => SECTION_COLORS[key]?.color || "#6b7280";
const sBg = (key) => SECTION_COLORS[key]?.bg || "rgba(107,114,128,0.07)";

const EMOJIS = [
  "🥤",
  "🥗",
  "🥪",
  "🍓",
  "🥣",
  "🥬",
  "🍊",
  "🫐",
  "⚡",
  "✨",
  "🌟",
  "🌸",
  "🥦",
  "💪",
  "🍑",
  "🔥",
  "🌯",
  "🍸",
  "🥛",
  "🫖",
  "🍵",
  "🧃",
  "🍋",
  "🥝",
  "🍇",
  "🍰",
  "🧁",
  "🍩",
  "🥞",
  "🫙",
];

/* ── tiny shared input ── */
const Field = ({ label, value, onChange, placeholder, style = {} }) => (
  <div>
    <label
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 700,
        color: "#6b7280",
        marginBottom: 4,
        textTransform: "uppercase",
        letterSpacing: ".4px",
      }}
    >
      {label}
    </label>
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "9px 12px",
        borderRadius: 10,
        border: "1.5px solid rgba(45,139,78,0.2)",
        background: "rgba(255,255,255,0.8)",
        fontSize: 13,
        color: "#111827",
        outline: "none",
        fontFamily: "inherit",
        ...style,
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "#2D8B4E";
        e.target.style.boxShadow = "0 0 0 3px rgba(45,139,78,0.1)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "rgba(45,139,78,0.2)";
        e.target.style.boxShadow = "none";
      }}
    />
  </div>
);

/* ── emoji picker row ── */
const EmojiPicker = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
    {EMOJIS.map((e) => (
      <button
        key={e}
        type="button"
        onClick={() => onChange(e)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          border: "1.5px solid",
          borderColor: value === e ? "#2D8B4E" : "#e5e7eb",
          background: value === e ? "rgba(45,139,78,0.1)" : "white",
          fontSize: 17,
          cursor: "pointer",
          transition: "all 0.12s",
        }}
      >
        {e}
      </button>
    ))}
  </div>
);

export default function AdminManageCategories() {
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* ── Add Section state ── */
  const [showAddSec, setShowAddSec] = useState(false);
  const [secLabel, setSecLabel] = useState("");
  const [secEmoji, setSecEmoji] = useState("📦");
  const [secKeyManual, setSecKeyManual] = useState("");
  const [secKeyAuto, setSecKeyAuto] = useState(true);

  /* ── Add Subcategory state (one panel open at a time) ── */
  const [openCatFor, setOpenCatFor] = useState(null); // sectionKey
  const [catLabel, setCatLabel] = useState("");
  const [catEmoji, setCatEmoji] = useState("🍃");
  const [catKeyManual, setCatKeyManual] = useState("");
  const [catKeyAuto, setCatKeyAuto] = useState(true);

  /* ── Delete confirm ── */
  const [delTarget, setDelTarget] = useState(null); // { type, sectionKey, categoryKey?, label }

  /* computed slugs */
  const secSlug = secKeyAuto ? toSlug(secLabel) : secKeyManual;
  const catSlug = catKeyAuto ? toSlug(catLabel) : catKeyManual;

  /* ── fetch ── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/products/sections/all");
      setSections(res.data?.sections || []);
    } catch {
      toast.error("Failed to load menu structure");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* sync slug when label changes */
  useEffect(() => {
    if (secKeyAuto) setSecKeyManual(toSlug(secLabel));
  }, [secLabel, secKeyAuto]);
  useEffect(() => {
    if (catKeyAuto) setCatKeyManual(toSlug(catLabel));
  }, [catLabel, catKeyAuto]);

  /* ── reset cat form ── */
  const resetCatForm = () => {
    setCatLabel("");
    setCatEmoji("🍃");
    setCatKeyManual("");
    setCatKeyAuto(true);
  };

  /* ── Add Section ── */
  const addSection = async () => {
    if (!secLabel.trim()) {
      toast.error("Section name is required");
      return;
    }
    if (!secSlug) {
      toast.error("Section key cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await api.post("/products/sections/manage", {
        action: "add_section",
        section: { key: secSlug, label: secLabel.trim(), emoji: secEmoji },
      });
      toast.success(`Section "${secLabel}" added!`);
      setShowAddSec(false);
      setSecLabel("");
      setSecEmoji("📦");
      setSecKeyManual("");
      setSecKeyAuto(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add section");
    } finally {
      setSaving(false);
    }
  };

  /* ── Add Subcategory ── */
  const addCategory = async (sectionKey) => {
    if (!catLabel.trim()) {
      toast.error("Subcategory name is required");
      return;
    }
    if (!catSlug) {
      toast.error("Subcategory key cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await api.post("/products/sections/manage", {
        action: "add_category",
        sectionKey,
        category: { key: catSlug, label: catLabel.trim(), emoji: catEmoji },
      });
      toast.success(`Subcategory "${catLabel}" added!`);
      setOpenCatFor(null);
      resetCatForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add subcategory");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const doDelete = async () => {
    if (!delTarget) return;
    setSaving(true);
    try {
      if (delTarget.type === "category") {
        await api.post("/products/sections/manage", {
          action: "delete_category",
          sectionKey: delTarget.sectionKey,
          categoryKey: delTarget.categoryKey,
        });
        toast.success(`Subcategory "${delTarget.label}" deleted`);
      } else {
        await api.post("/products/sections/manage", {
          action: "delete_section",
          sectionKey: delTarget.sectionKey,
        });
        toast.success(`Section "${delTarget.label}" deleted`);
      }
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const BUILT_IN = ["juices", "salads", "sandwiches", "smoothies"];

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      <AdminNavbar />
      <div
        style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px 60px" }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 28,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate("/admin/inventory")}
              style={{
                border: "1.5px solid rgba(45,139,78,0.3)",
                color: "#2D8B4E",
                borderRadius: 100,
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 600,
                background: "rgba(255,255,255,0.7)",
                cursor: "pointer",
              }}
            >
              ← Inventory
            </button>
            <div>
              <h1
                style={{
                  fontSize: 26,
                  fontWeight: 900,
                  color: "#14532d",
                  margin: 0,
                  letterSpacing: "-0.4px",
                }}
              >
                Menu Structure
              </h1>
              <p
                style={{
                  fontSize: 12,
                  color: "#9ca3af",
                  margin: 0,
                  marginTop: 2,
                }}
              >
                Add or remove sections and subcategories shown on the menu
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setShowAddSec(true);
              setSecLabel("");
              setSecEmoji("📦");
              setSecKeyManual("");
              setSecKeyAuto(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "10px 20px",
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
              color: "#fff",
              background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
              boxShadow: "0 4px 14px rgba(45,139,78,0.3)",
            }}
          >
            ➕ New Section
          </button>
        </div>

        {/* ── Add Section Form ── */}
        {showAddSec && (
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              padding: 20,
              marginBottom: 20,
              border: "2px solid rgba(45,139,78,0.2)",
              boxShadow: "0 4px 20px rgba(45,139,78,0.08)",
            }}
          >
            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#2D8B4E",
                textTransform: "uppercase",
                letterSpacing: ".8px",
                margin: "0 0 14px",
              }}
            >
              New Section
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <Field
                label="Section Name *"
                value={secLabel}
                onChange={(e) => setSecLabel(e.target.value)}
                placeholder="e.g. Desserts"
              />
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#6b7280",
                    marginBottom: 4,
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                  }}
                >
                  Key (auto-generated)
                </label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    value={secSlug}
                    onChange={(e) => {
                      setSecKeyManual(e.target.value);
                      setSecKeyAuto(false);
                    }}
                    placeholder="e.g. desserts"
                    style={{
                      flex: 1,
                      padding: "9px 12px",
                      borderRadius: 10,
                      border: "1.5px solid rgba(45,139,78,0.2)",
                      background: "rgba(255,255,255,0.8)",
                      fontSize: 13,
                      color: "#2D8B4E",
                      fontFamily: "monospace",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2D8B4E";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(45,139,78,0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "rgba(45,139,78,0.2)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  {!secKeyAuto && (
                    <button
                      onClick={() => setSecKeyAuto(true)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1.5px solid #e5e7eb",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6b7280",
                        background: "#f9fafb",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Auto
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 4 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#6b7280",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: ".4px",
                }}
              >
                Emoji
              </label>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <span style={{ fontSize: 28 }}>{secEmoji}</span>
                <input
                  value={secEmoji}
                  onChange={(e) => setSecEmoji(e.target.value)}
                  style={{
                    width: 54,
                    padding: "6px",
                    textAlign: "center",
                    fontSize: 20,
                    borderRadius: 8,
                    border: "1.5px solid #e5e7eb",
                    outline: "none",
                  }}
                />
              </div>
              <EmojiPicker value={secEmoji} onChange={setSecEmoji} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button
                onClick={addSection}
                disabled={saving}
                style={{
                  padding: "9px 22px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#fff",
                  background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "Saving…" : "✓ Add Section"}
              </button>
              <button
                onClick={() => setShowAddSec(false)}
                style={{
                  padding: "9px 18px",
                  borderRadius: 10,
                  border: "1.5px solid #e5e7eb",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#6b7280",
                  background: "#fff",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Sections List ── */}
        {loading ? (
          <div style={{ display: "grid", gap: 12 }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: 18,
                  padding: 20,
                  animation: "pulse 1.4s ease infinite",
                }}
              >
                <div
                  style={{
                    height: 20,
                    width: 160,
                    background: "#f0f0f0",
                    borderRadius: 6,
                    marginBottom: 12,
                  }}
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[90, 120, 80].map((w, j) => (
                    <div
                      key={j}
                      style={{
                        height: 30,
                        width: w,
                        borderRadius: 100,
                        background: "#f0f0f0",
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {sections.map((sec) => {
              const color = sColor(sec.key);
              const bg = sBg(sec.key);
              const isOpen = openCatFor === sec.key;

              return (
                <div
                  key={sec.key}
                  style={{
                    background: "#fff",
                    borderRadius: 18,
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  {/* Section header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px 20px 12px",
                      borderBottom:
                        sec.categories.length > 0 || isOpen
                          ? "1px solid #f5f5f5"
                          : "none",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 20,
                          background: bg,
                          border: `1.5px solid ${color}25`,
                          flexShrink: 0,
                        }}
                      >
                        {sec.emoji}
                      </div>
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 800,
                              color: "#1f2937",
                            }}
                          >
                            {sec.label}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#9ca3af",
                              background: "#f3f4f6",
                              padding: "2px 7px",
                              borderRadius: 6,
                              fontFamily: "monospace",
                            }}
                          >
                            {sec.key}
                          </span>
                        </div>
                        <p
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                            margin: 0,
                            marginTop: 2,
                          }}
                        >
                          {sec.categories.length} subcategor
                          {sec.categories.length !== 1 ? "ies" : "y"}
                          {sec.totalCount > 0 &&
                            ` · ${sec.totalCount} products`}
                        </p>
                      </div>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <button
                        onClick={() => {
                          if (isOpen) {
                            setOpenCatFor(null);
                            resetCatForm();
                          } else {
                            setOpenCatFor(sec.key);
                            resetCatForm();
                          }
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "7px 14px",
                          borderRadius: 10,
                          border: `1.5px solid ${color}`,
                          color,
                          background: isOpen ? bg : "white",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {isOpen ? "✕ Cancel" : "➕ Add Subcategory"}
                      </button>
                      {!BUILT_IN.includes(sec.key) && (
                        <button
                          onClick={() =>
                            setDelTarget({
                              type: "section",
                              sectionKey: sec.key,
                              label: sec.label,
                            })
                          }
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            border: "1.5px solid #fee2e2",
                            background: "#fff",
                            color: "#f87171",
                            cursor: "pointer",
                            fontSize: 14,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#fef2f2";
                            e.currentTarget.style.color = "#ef4444";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fff";
                            e.currentTarget.style.color = "#f87171";
                          }}
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Subcategory pills */}
                  {sec.categories.length > 0 && (
                    <div
                      style={{
                        padding: "12px 20px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      {sec.categories.map((cat) => (
                        <div
                          key={cat.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 12px",
                            borderRadius: 100,
                            border: `1.5px solid ${color}30`,
                            background: bg,
                            fontSize: 12,
                            fontWeight: 600,
                            color,
                            position: "relative",
                          }}
                          className="cat-pill"
                        >
                          <span style={{ fontSize: 14 }}>{cat.emoji}</span>
                          <span>{cat.label}</span>
                          <span
                            style={{
                              fontSize: 9,
                              fontFamily: "monospace",
                              color: "#9ca3af",
                              fontWeight: 400,
                            }}
                          >
                            ({cat.key})
                          </span>
                          {cat.count > 0 && (
                            <span
                              style={{
                                padding: "1px 6px",
                                borderRadius: 20,
                                fontSize: 9,
                                fontWeight: 800,
                                background: `${color}18`,
                                color,
                              }}
                            >
                              {cat.count}
                            </span>
                          )}
                          <button
                            onClick={() =>
                              setDelTarget({
                                type: "category",
                                sectionKey: sec.key,
                                categoryKey: cat.key,
                                label: cat.label,
                              })
                            }
                            style={{
                              marginLeft: 2,
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              border: "none",
                              background: "transparent",
                              color: "#f87171",
                              cursor: "pointer",
                              fontSize: 11,
                              fontWeight: 900,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 0,
                              opacity: 0,
                              transition: "opacity 0.15s",
                            }}
                            className="cat-del"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {sec.categories.length === 0 && !isOpen && (
                    <div style={{ padding: "10px 20px 14px" }}>
                      <span
                        style={{
                          fontSize: 12,
                          color: "#d1d5db",
                          fontStyle: "italic",
                        }}
                      >
                        No subcategories yet — add one above
                      </span>
                    </div>
                  )}

                  {/* Add Subcategory Form */}
                  {isOpen && (
                    <div
                      style={{
                        margin: "0 16px 16px",
                        padding: 16,
                        borderRadius: 14,
                        background: bg,
                        border: `1.5px solid ${color}25`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color,
                          textTransform: "uppercase",
                          letterSpacing: ".8px",
                          margin: "0 0 12px",
                        }}
                      >
                        New Subcategory in {sec.label}
                      </p>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 10,
                          marginBottom: 10,
                        }}
                      >
                        <Field
                          label="Name *"
                          value={catLabel}
                          onChange={(e) => setCatLabel(e.target.value)}
                          placeholder="e.g. Vegan Bowls"
                        />
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#6b7280",
                              marginBottom: 4,
                              textTransform: "uppercase",
                              letterSpacing: ".4px",
                            }}
                          >
                            Key (slug) *
                          </label>
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              alignItems: "center",
                            }}
                          >
                            <input
                              value={catSlug}
                              onChange={(e) => {
                                setCatKeyManual(e.target.value);
                                setCatKeyAuto(false);
                              }}
                              placeholder="e.g. vegan-bowls"
                              style={{
                                flex: 1,
                                padding: "9px 12px",
                                borderRadius: 10,
                                border: "1.5px solid rgba(45,139,78,0.2)",
                                background: "rgba(255,255,255,0.9)",
                                fontSize: 12,
                                color: "#2D8B4E",
                                fontFamily: "monospace",
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = "#2D8B4E";
                                e.target.style.boxShadow =
                                  "0 0 0 3px rgba(45,139,78,0.1)";
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor =
                                  "rgba(45,139,78,0.2)";
                                e.target.style.boxShadow = "none";
                              }}
                            />
                            {!catKeyAuto && (
                              <button
                                onClick={() => setCatKeyAuto(true)}
                                style={{
                                  padding: "6px 8px",
                                  borderRadius: 8,
                                  border: "1.5px solid #e5e7eb",
                                  fontSize: 11,
                                  color: "#6b7280",
                                  background: "white",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Auto
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#6b7280",
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: ".4px",
                          }}
                        >
                          Emoji
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            marginBottom: 6,
                          }}
                        >
                          <span style={{ fontSize: 24 }}>{catEmoji}</span>
                          <input
                            value={catEmoji}
                            onChange={(e) => setCatEmoji(e.target.value)}
                            style={{
                              width: 46,
                              padding: "5px",
                              textAlign: "center",
                              fontSize: 18,
                              borderRadius: 8,
                              border: "1.5px solid #e5e7eb",
                              outline: "none",
                            }}
                          />
                        </div>
                        <EmojiPicker value={catEmoji} onChange={setCatEmoji} />
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => addCategory(sec.key)}
                          disabled={saving}
                          style={{
                            padding: "8px 20px",
                            borderRadius: 10,
                            border: "none",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#fff",
                            background: `linear-gradient(135deg,${color},${color}cc)`,
                            opacity: saving ? 0.6 : 1,
                          }}
                        >
                          {saving ? "Saving…" : "✓ Add Subcategory"}
                        </button>
                        <button
                          onClick={() => {
                            setOpenCatFor(null);
                            resetCatForm();
                          }}
                          style={{
                            padding: "8px 16px",
                            borderRadius: 10,
                            border: "1.5px solid #e5e7eb",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            background: "white",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Delete Confirm Modal ── */}
        {delTarget && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(4px)",
              }}
              onClick={() => setDelTarget(null)}
            />
            <div
              style={{
                position: "relative",
                background: "#fff",
                borderRadius: 20,
                padding: 24,
                width: "100%",
                maxWidth: 380,
                boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}
              >
                ⚠️
              </div>
              <h3
                style={{
                  fontSize: 17,
                  fontWeight: 900,
                  textAlign: "center",
                  color: "#111827",
                  margin: "0 0 8px",
                }}
              >
                Confirm Delete
              </h3>
              <p
                style={{
                  fontSize: 13,
                  color: "#6b7280",
                  textAlign: "center",
                  lineHeight: 1.6,
                  margin: "0 0 20px",
                }}
              >
                Delete{" "}
                {delTarget.type === "section" ? "section" : "subcategory"}{" "}
                <strong style={{ color: "#111" }}>"{delTarget.label}"</strong>?
                {delTarget.type === "section"
                  ? " This will also remove all its subcategories."
                  : " Products using this subcategory will not be deleted but may not appear in the menu."}
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setDelTarget(null)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    borderRadius: 11,
                    border: "1.5px solid #e5e7eb",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#6b7280",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={doDelete}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: "10px",
                    borderRadius: 11,
                    border: "none",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    background: "linear-gradient(135deg,#ef4444,#dc2626)",
                    cursor: "pointer",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .cat-pill:hover .cat-del { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
